import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { BarChart3, Download, TrendingUp, DollarSign, Users, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

import API_URL from '../utils/api';

const Analytics = () => {
    const [analytics, setAnalytics] = useState(null);
    const [payrollReport, setPayrollReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    useEffect(() => {
        fetchAnalytics();
    }, []);

    useEffect(() => {
        fetchPayrollReport();
    }, [selectedMonth, selectedYear]);

    const fetchAnalytics = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/analytics/dashboard`, { withCredentials: true });
            setAnalytics(data);
        } catch (error) {
            logger.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPayrollReport = async () => {
        try {
            const { data } = await axios.get(
                `${API_URL}/api/analytics/payroll-report?month=${selectedMonth}&year=${selectedYear}`,
                { withCredentials: true }
            );
            setPayrollReport(data);
        } catch (error) {
            logger.error('Error fetching payroll report:', error);
        }
    };

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading analytics...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="analytics-page">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Analytics & Reports
                </h1>
                <p className="text-slate-500 mt-1">HR and payroll insights at a glance</p>
            </div>

            {/* Key Metrics */}
            {analytics && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card className="rounded-none border-slate-200" data-testid="metric-employees">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Employees</p>
                                    <p className="text-3xl font-black text-slate-900 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                        {analytics.employees.total}
                                    </p>
                                </div>
                                <div className="p-3 bg-blue-100">
                                    <Users className="h-6 w-6 text-brand" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="rounded-none border-slate-200" data-testid="metric-gross">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Gross Payroll</p>
                                    <p className="text-3xl font-black text-slate-900 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                        ${(analytics.payroll.current_month.total_gross || 0).toLocaleString()}
                                    </p>
                                </div>
                                <div className="p-3 bg-green-100">
                                    <DollarSign className="h-6 w-6 text-green-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="rounded-none border-slate-200" data-testid="metric-deductions">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Deductions</p>
                                    <p className="text-3xl font-black text-red-600 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                        ${(analytics.payroll.current_month.total_deductions || 0).toLocaleString()}
                                    </p>
                                </div>
                                <div className="p-3 bg-red-100">
                                    <TrendingUp className="h-6 w-6 text-red-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="rounded-none border-slate-200" data-testid="metric-pending-leaves">
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pending Leaves</p>
                                    <p className="text-3xl font-black text-amber-600 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                        {analytics.leaves.pending}
                                    </p>
                                </div>
                                <div className="p-3 bg-amber-100">
                                    <Calendar className="h-6 w-6 text-amber-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Payroll Trend */}
                <Card className="rounded-none border-slate-200" data-testid="payroll-trend-chart">
                    <CardHeader className="border-b border-slate-200">
                        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <TrendingUp className="h-5 w-5 text-brand" />
                            Payroll Trend
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        {analytics?.payroll?.trend?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={analytics.payroll.trend.map(item => ({
                                    name: `${months[item._id.month - 1]?.slice(0, 3)} ${item._id.year}`,
                                    gross: item.total_gross,
                                    net: item.total_net
                                }))}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#64748b" />
                                    <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                                    <Tooltip 
                                        contentStyle={{ 
                                            border: '1px solid #e2e8f0', 
                                            borderRadius: 0,
                                            backgroundColor: '#fff'
                                        }} 
                                    />
                                    <Line type="monotone" dataKey="gross" stroke="#94a3b8" strokeWidth={2} name="Gross" />
                                    <Line type="monotone" dataKey="net" stroke="#2563eb" strokeWidth={2} name="Net" />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-[300px] text-slate-500">
                                No payroll data available
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Department Distribution */}
                <Card className="rounded-none border-slate-200" data-testid="department-distribution-chart">
                    <CardHeader className="border-b border-slate-200">
                        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <Users className="h-5 w-5 text-brand" />
                            Department Distribution
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        {analytics?.department_distribution?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={analytics.department_distribution.map(item => ({
                                            name: item._id,
                                            value: item.count
                                        }))}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {analytics.department_distribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{ 
                                            border: '1px solid #e2e8f0', 
                                            borderRadius: 0,
                                            backgroundColor: '#fff'
                                        }} 
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-[300px] text-slate-500">
                                No department data available
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Monthly Payroll Report */}
            <Card className="rounded-none border-slate-200" data-testid="monthly-report">
                <CardHeader className="border-b border-slate-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <BarChart3 className="h-5 w-5 text-brand" />
                            Monthly Payroll Report
                        </CardTitle>
                        <div className="flex items-center gap-3">
                            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                                <SelectTrigger className="w-[140px] rounded-none" data-testid="report-month-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-none">
                                    {months.map((m, i) => (
                                        <SelectItem key={m} value={(i + 1).toString()}>{m}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Input
                                type="number"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                className="w-[100px] rounded-none"
                                data-testid="report-year-input"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-6">
                    {payrollReport && payrollReport.records.length > 0 ? (
                        <>
                            {/* Summary */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 border border-slate-200">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Employees</p>
                                    <p className="text-xl font-bold text-slate-900">{payrollReport.summary.total_employees}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Gross</p>
                                    <p className="text-xl font-bold text-slate-900">${payrollReport.summary.total_gross.toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Deductions</p>
                                    <p className="text-xl font-bold text-red-600">${payrollReport.summary.total_deductions.toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Net</p>
                                    <p className="text-xl font-bold text-green-600">${payrollReport.summary.total_net.toLocaleString()}</p>
                                </div>
                            </div>

                            {/* Chart */}
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={payrollReport.records.map(r => ({
                                    name: r.employee_name.split(' ')[0],
                                    gross: r.gross_salary,
                                    deductions: r.total_deductions,
                                    net: r.net_salary
                                }))}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#64748b" />
                                    <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                                    <Tooltip 
                                        contentStyle={{ 
                                            border: '1px solid #e2e8f0', 
                                            borderRadius: 0,
                                            backgroundColor: '#fff'
                                        }} 
                                    />
                                    <Bar dataKey="gross" fill="#94a3b8" name="Gross" />
                                    <Bar dataKey="deductions" fill="#ef4444" name="Deductions" />
                                    <Bar dataKey="net" fill="#2563eb" name="Net" />
                                </BarChart>
                            </ResponsiveContainer>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-[200px] text-slate-500">
                            No payroll data for {months[selectedMonth - 1]} {selectedYear}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Analytics;
